import React, { useEffect, useState } from 'react';
import { getInvoices } from '../api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const fmtRands = (cents) => `R${(cents / 100).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;

export default function InvoicesPanel({ isMobile }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getInvoices()
      .then(setInvoices)
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = (id) => {
    window.open(`${API_BASE}/invoices/${id}/html`, '_blank');
  };

  const statusStyle = (status) => {
    if (status === 'paid') return { background: 'rgba(26,122,74,0.1)', color: 'var(--green)' };
    if (status === 'cancelled') return { background: 'rgba(176,48,32,0.1)', color: 'var(--red)' };
    return { background: 'var(--s-w08)', color: 'var(--amber)' };
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.title}>Invoices</div>
        </div>
        <div style={styles.empty}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.title}>Invoices</div>
        <div style={styles.count}>{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</div>
      </div>

      {invoices.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyTitle}>No invoices yet</div>
          <div style={styles.emptyText}>Invoices appear here when you purchase reports.</div>
        </div>
      ) : (
        <div style={styles.list}>
          {invoices.map(inv => (
            <div key={inv.id} style={styles.card}>
              <div style={styles.cardTop}>
                <div style={styles.refCol}>
                  <div style={styles.refNumber}>{inv.ref_number}</div>
                  <div style={styles.cardDate}>
                    {new Date(inv.created_at).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                </div>
                <div style={styles.itemsCol}>
                  {(inv.lines || []).map((l, i) => (
                    <div key={i} style={styles.lineItem}>{l.description}</div>
                  ))}
                </div>
                <div style={styles.amountCol}>
                  <div style={styles.amount}>{fmtRands(inv.total_cents)}</div>
                  <span style={{ ...styles.statusPill, ...statusStyle(inv.status) }}>
                    {inv.status}
                  </span>
                </div>
              </div>
              <div style={styles.cardBottom}>
                <span style={styles.method}>
                  {inv.payment_method === 'eft' ? 'EFT' : inv.payment_method === 'payfast' ? 'Card' : inv.payment_method || '—'}
                </span>
                <button onClick={() => handleDownload(inv.id)} style={styles.downloadBtn}>
                  Download invoice
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    background: 'var(--cream)',
    padding: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottom: '1px solid var(--rule)',
  },
  title: {
    fontFamily: "'News Cycle', serif",
    fontSize: 24,
    fontWeight: 700,
    color: 'var(--ink)',
  },
  count: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    color: 'var(--muted)',
    letterSpacing: '0.5px',
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyTitle: {
    fontFamily: "'News Cycle', serif",
    fontSize: 20,
    color: 'var(--ink)',
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 13,
    color: 'var(--muted)',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    maxWidth: 700,
  },
  card: {
    background: 'var(--white)',
    border: '1px solid var(--rule)',
    borderRadius: 6,
    padding: '16px 20px',
  },
  cardTop: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
  },
  refCol: {
    flexShrink: 0,
    minWidth: 140,
  },
  refNumber: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--ink)',
  },
  cardDate: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    color: 'var(--muted)',
    marginTop: 4,
  },
  itemsCol: {
    flex: 1,
    minWidth: 0,
  },
  lineItem: {
    fontSize: 13,
    color: 'var(--ink)',
    lineHeight: 1.6,
  },
  amountCol: {
    textAlign: 'right',
    flexShrink: 0,
  },
  amount: {
    fontFamily: "'News Cycle', serif",
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--ink)',
  },
  statusPill: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: "'DM Mono', monospace",
    marginTop: 4,
  },
  cardBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 10,
    borderTop: '1px solid var(--rule)',
  },
  method: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    color: 'var(--muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  downloadBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--ink)',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Work Sans', sans-serif",
    padding: 0,
  },
};
